'use client'

import { useEffect, useRef, createContext, useContext } from 'react'
import { Header } from '@/app/(frontend)/Header'
import type { Tenant } from '@/payload-types'
import {
  applyTenantFavicon,
  applyTenantFonts,
  generateTenantCSS,
  injectTenantStyles,
  removeExistingTenantStyles,
} from '@/utilities/tenantStyles'
import { StytchUser } from '@/utilities/stytch/server'
import { Footer } from '@/app/(frontend)/Footer'
import { cn } from '@/utilities/ui'
import { PatientPortalScripts } from '@/providers/PatientPortalScripts'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'

interface TenantContextType {
  tenant: Tenant | null
  user: StytchUser | null
  isOnboarding: boolean
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantLayoutClient')
  }
  return context
}

// Safe version that doesn't throw error when not in context
export function useTenantSafe() {
  return useContext(TenantContext)
}

export { TenantContext }

interface TenantLayoutClientProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  className?: string
  contentClassName?: string
  contentPadding?: string
  tenant: Tenant | null
  user: StytchUser | null
  isOnboarding?: boolean
  showFooter?: boolean
  showHeader?: boolean
}

export function TenantLayoutClient({
  children,
  fallback,
  className = 'h-full flex flex-col min-h-screen',
  contentClassName = 'size-full overflow-y-auto flex flex-1 flex-col items-center',
  contentPadding = 'px-4 py-6 md:py-8',
  tenant,
  user,
  isOnboarding = false,
  showFooter = true,
  showHeader = true,
}: Readonly<TenantLayoutClientProps>) {
  const lastTenantId = useRef<string | null>(null)

  useEffect(() => {
    if (!tenant) return

    const currentTenantId = String(tenant.id)
    const isInitialMount = lastTenantId.current === null
    const tenantHasChanged = lastTenantId.current !== currentTenantId

    if (tenantHasChanged) {
      lastTenantId.current = currentTenantId

      try {
        applyTenantFavicon(tenant as never)

        if (isInitialMount) {
          const existingStyles = document.getElementById('tenant-styles')
          if (existingStyles) {
            return
          }
        }

        // Either: (1) initial mount with no SSR styles, OR (2) actual tenant change
        removeExistingTenantStyles()
        const css = generateTenantCSS(tenant as never)
        injectTenantStyles(css)
        applyTenantFonts(tenant as never)
      } catch (error) {
        console.error('Error applying tenant styles:', error)
        trackEvent('tenant:apply_styles_fail', {
          tenantId: tenant.id,
          tenantSlug: tenant.slug,
          ...errorAnalyticsPayload(error),
        })
      }
    }
  }, [tenant])

  if (tenant) {
    return (
      <TenantContext.Provider value={{ tenant, user, isOnboarding }}>
        <div className={cn('flex flex-col min-h-screen', className)} id="tenant-layout-client">
          {showHeader && <Header />}
          <div
            className={cn(
              contentClassName,
              contentPadding,
              tenant?.slug === 'client' ? 'bg-special-grey' : 'bg-tenant-page-background',
            )}
            tabIndex={0}
          >
            {children}
          </div>
          {showFooter && <Footer />}
        </div>
        {tenant.slug !== 'client' && (
          <PatientPortalScripts enableZendeskChat={tenant.enableZendeskChat} />
        )}
      </TenantContext.Provider>
    )
  }

  // If no tenant found, render fallback or return null
  return fallback || null
}
